// @ts-strict-ignore
import { BaseArticleLocationProcessor } from './base-article-location.processor';
import { IArticleLocationProcessor, InsertedArticleData } from './i-article-location.processor';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ReplaceReferenceArticleLocationVO } from '@article/model/vo/replace-reference-article-location.vo';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { NumberUtils } from '@shared/util/number-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ProjectHelper } from '@project/helper/project.helper';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleInsertProcessorRegistry } from './prepare/insert/article-insert-processor-registry';
import { ProjectModel } from '@project/model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { DetachArticleUsageService } from '@project/service/detach-article-usage.service';
import { ReplaceBusbarArticleHelper } from '@article/helper/replace/busbar/replace-busbar-article.helper';
import { Busbar } from '@article/helper/replace/busbar/replace-busbar-data';
import { InsertArticleUtils } from '../util/insert-article-utils';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { ReplaceBusbarService } from '@article/helper/replace/busbar/replace-busbar.service';
import { AddArticleError } from './add-article-error';

export class ReplaceArticleOnRailLocationProcessor extends BaseArticleLocationProcessor<ReplaceReferenceArticleLocationVO> implements IArticleLocationProcessor {

  private static readonly LANGUAGE_KEY_ERROR_CONNECT_TO_MOUNTING_RAIL: string =
    'articleLocationProcessor.message.error_connect_to_mounting_rail';
  private static readonly LANGUAGE_KEY_ERROR_UNKNOWN_CONNECTION: string =
    'articleLocationProcessor.message.error_unknown_connection';
  private static readonly LANGUAGE_KEY_ERROR_ARTICLE_NOT_FOUND: string =
    'articleLocationProcessor.replace.message.error_article_not_found';
  private static readonly LANGUAGE_KEY_ERROR_NOT_ENOUGH_FREE_SPACE: string =
    'articleLocationProcessor.replace.message.error_not_enough_free_space';
  private static readonly LANGUAGE_KEY_ERROR_DIFFERENT_WIDTH: string =
    'articleLocationProcessor.replace.message.error_different_width';

  private _articleContainerToReplace: ArticleContainer;
  private _articleContainersToReplace: ArticleContainer[];
  private _isConnectedOnRail = false

  constructor(
    articleLocation: ReplaceReferenceArticleLocationVO,
    article: ArticleVO,
    insertProcessorRegistry: ArticleInsertProcessorRegistry,
    projectHelper: ProjectHelper,
    localeResourceManager: LocaleResourceManager,
    insertArticleHelperFactory: InsertArticleHelperFactory,
    connectionInfoService: ConnectionInfoService,
    replaceBusbarHelper: ReplaceBusbarArticleHelper,
    articleManager: ArticleManager,
    projectModel: ProjectModel,
    private insertArticleUtils: InsertArticleUtils,
    private containerService: ContainerService,
    private applicationRefreshService: ApplicationRefreshService,
    private detachArticleUsageService: DetachArticleUsageService,
    private replaceBusbarService: ReplaceBusbarService
  ) {
    super(
      projectModel,
      articleManager,
      insertProcessorRegistry,
      projectHelper,
      localeResourceManager,
      articleLocation,
      article,
      connectionInfoService,
      replaceBusbarHelper,
      insertArticleHelperFactory,
    )
  }

  private get articleLocation(): ReplaceReferenceArticleLocationVO {
    return this._articleLocation;
  }

  public async process(): Promise<InsertedArticleData> {
    await this.loadArea();
    await this.checkIsConnectedOnRail();
    await super.loadAndProcessFullArticle();
    await super.createAndRefreshWrapper();
    return this.insertArticle()
  }

  private async checkIsConnectedOnRail(): Promise<void> {
    if (!this._isConnectedOnRail) {
      await super.processFailure(`Could not load connect to mounting rail`);
      const errorMessage: string = this.localeResourceManager.getValueByKey(
        ReplaceArticleOnRailLocationProcessor.LANGUAGE_KEY_ERROR_CONNECT_TO_MOUNTING_RAIL);
      throw new AddArticleError(errorMessage);
    }
  }

  private async loadArea(): Promise<void> {
    this._articleContainerToReplace = this.containerService.findArticleContainerInGlobal(this.articleLocation.articleUsageIdToReplace);
    if (!this._articleContainerToReplace) {
      const errorMessage: string = this.localeResourceManager.getValueByKey(
        ReplaceArticleOnRailLocationProcessor.LANGUAGE_KEY_ERROR_ARTICLE_NOT_FOUND);
      await this.processFailure(errorMessage);
      throw new AddArticleError(errorMessage)
    }

    if (ArticleUsageUtils.isCompositePartArticleUsage(this._articleContainerToReplace.articleUsage)
      && !(this.projectHelper.isCompositePartArticleModificationAllowed(this._articleContainerToReplace))) {
      this._articleContainersToReplace = this.containerService.getBaseCompositePartArticles(this._articleContainerToReplace);
    } else {
      this._articleContainersToReplace = [this._articleContainerToReplace];
    }

    const parentConnectionUsageIds: MetaElementUsageIdentityVO[] =
      ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(this._articleContainerToReplace.articleUsage);
    if (parentConnectionUsageIds.length > 0) {
      const parentConnectionId: MetaElementUsageIdentityVO = parentConnectionUsageIds[0];
      this._articleContainer = this.containerService.findArticleContainerInGlobal(parentConnectionId.articleUsageId);
      this._acceptingConnectionArea =
        this._articleContainer.findMetaElementByName(parentConnectionId.metaElementName) as ConnectionAreaVO;
      if (ConnectionInfoUtils.isRangeOnLineConnection(this._acceptingConnectionArea)) {
        this.initInsertArticleHelper();
        this._isConnectedOnRail = true;
      }
    }
  }


  private async insertArticle(): Promise<InsertedArticleData> {
      const givingCubes: Connection[] = this.findAllGivingCubesForConnect();
      if (givingCubes && givingCubes.length > 0 && givingCubes[0].compositionRectangle) {
        const articleWidth: number = this.calculateArticleWidth(givingCubes);
        const canReplaceWithDifferentWidth: boolean = this.checkReplaceWidth(articleWidth);

        if (!canReplaceWithDifferentWidth) {
          const errorMessage = this.localeResourceManager.getValueByKey(
            ReplaceArticleOnRailLocationProcessor.LANGUAGE_KEY_ERROR_DIFFERENT_WIDTH);
          await super.processFailure(`Could not replace the article.`);
          throw new AddArticleError(errorMessage);
        } else {
          const childReplacedContainerIds: string[] = this.getAllChildContainerIdsForReplacedArticles();
          let excludedArticleIds: string[] = ArticleContainerUtils.extractArticleGUIDs(this._articleContainersToReplace);
          excludedArticleIds = excludedArticleIds.concat(childReplacedContainerIds);

          let moveMap: Dictionary<string, Vector3> = null;
          let articleLine: AdvancedLine = null;
          const rotationAngle: number = this.getRotateAngleOfBaseContainer(
            this._articlesWrapper.baseArticleContainer,
            this.getRotationAngleByReplacedArticle()
          );
          const replaceArticleLine: AdvancedLine = this._insertArticleHelper.calculateCommonArticlesLine(this._articleContainersToReplace);
          const busbarsToRecreate: Busbar[] = this.getBusbarsToRecreate();
          if (replaceArticleLine) {
            const leftArticleContainer: ArticleContainer = this._insertArticleHelper.getLeftArticleContainerByPoint(replaceArticleLine.start);
            const leftLine: AdvancedLine = this._insertArticleHelper.getArticleLine(leftArticleContainer);
            let overlapDistance: number = 0;
            // placement point should be recalculated in case overlapping already take place
            if (leftLine && leftLine.hasLine(replaceArticleLine, false)) {
              const dummyLine: AdvancedLine = AdvancedLine.createInstanceByArticle(this._article);
              if (rotationAngle === 180) {
                dummyLine.reverseOverlapProperties();
              }
              const maxOverlapDistance: number = AdvancedLine.calculateMaxOverlapLength(leftLine, dummyLine);
              const replacedOverlapDistance: number = replaceArticleLine.start.distanceTo(leftLine.end);
              if (maxOverlapDistance === 0) {
                overlapDistance = -replacedOverlapDistance;
              } else if (replacedOverlapDistance > maxOverlapDistance) {
                overlapDistance = maxOverlapDistance - replacedOverlapDistance;
              }
            }
            const articleOffset: Vector3 = MathUtils.calculateOffset(articleWidth, this._insertArticleHelper.baseLine);
            const startOffset: Vector3 = MathUtils.calculateOffset(overlapDistance, this._insertArticleHelper.baseLine);
            const startArticlePoint: Vector3 = replaceArticleLine.start.clone().sub(startOffset);
            articleLine = AdvancedLine.createInstanceByArticle(this._article, startArticlePoint, startArticlePoint.clone().add(articleOffset));
            if (rotationAngle === 180) {
              articleLine.reverseOverlapProperties();
            }

            const maxAllowedMoveOffsetMap: Dictionary<string, number> = this.replaceBusbarService.getMaxAllowedMoveOffsetMap(busbarsToRecreate, this._insertArticleHelper);
            moveMap = this._insertArticleHelper.replaceArticle(replaceArticleLine, articleLine, excludedArticleIds, true, maxAllowedMoveOffsetMap);
           }

          if (moveMap) {
            const busbarsChildrenMoveMap: Dictionary<string, Vector3> = this.replaceBusbarService.getChildrenMoveMap(busbarsToRecreate, moveMap, this._insertArticleHelper);
            this.insertArticleUtils.moveArticlesByMap(busbarsChildrenMoveMap);
            InsertArticleUtils.mergeMoveMap(moveMap, busbarsChildrenMoveMap);

            const acceptingCube: Connection = this._insertArticleHelper.createConnectionOnLine(articleLine, this._acceptingConnectionArea);

            this._insertProcessor.process(
              this._articlesWrapper.articleContainers,
              this._articlesWrapper.baseArticleContainers,
              articleLine,
              acceptingCube,
              givingCubes,
              rotationAngle,
              excludedArticleIds,
              true
            );

            // rotation-info property will be updated by _insertProcessor.process()-method in case rotation-angle is set and article
            // is rotated so we don't need to additionally call ArticleUsageUtils.updateAppliedRotationAngle() here
            // if(_articleLocation.rotationAngle != 0) ArticleUsageUtils.updateAppliedRotationAngle(baseArticleContainer.articleUsage,
            // _articleLocation.rotationAngle);

            acceptingCube.articleContainer.disposeConnectionByName(acceptingCube.id);

            if (this.articleLocation.detachReplacedArticle) {
              const detachedArticle = await this.detachArticleUsageService.detachArticleUsageFromProjectById(this._articleContainerToReplace.articleGUID);
              if (detachedArticle) {
                this.applicationRefreshService.notifyProjectChange();
              }
            }
            const data: InsertedArticleData = {
              baseArticleGUID: this._articlesWrapper.baseArticleContainer.articleGUID,
              insertedArticleGUIDs: ArticleContainerUtils.extractArticleGUIDs(this._articlesWrapper.articleContainers),
              moveArticlesMap: moveMap,
              articleLocation: this.articleLocation,
              createUndoOperation: true,
              undoOperations: null,
              isAccessoriesAdded: false,
            };
            return data;
          } else {
            const errorMessage = this.localeResourceManager.getValueByKey(
              ReplaceArticleOnRailLocationProcessor.LANGUAGE_KEY_ERROR_NOT_ENOUGH_FREE_SPACE);
            await super.processFailure(errorMessage);
            throw new AddArticleError(errorMessage);
      }
        }
      } else {
        const errorMessage: string = this.localeResourceManager.getValueByKey(
          ReplaceArticleOnRailLocationProcessor.LANGUAGE_KEY_ERROR_UNKNOWN_CONNECTION,
          {'0': this._articlesWrapper.article.articleNo, '1': this._givingConnectionAreas[0].name});
        await super.processFailure(`Unknown connection for existing cubes.`);
        throw new AddArticleError(errorMessage);
      }
  }

  private getAllChildContainerIdsForReplacedArticles(): string[] {
    const result: string[] = [];
    for (const container of this._articleContainersToReplace) {
      ArrayUtils.mergeArrays(result, ArticleUsageUtils.getAcceptingAreaIds(container.articleUsage));
    }
    return result;
  }

  private checkReplaceWidth(articleWidth: number): boolean {
    if (this.projectHelper.isReplaceWithDifferentWidthAllowed()) {
      return true;
    }
    for (const containerToReplace of this._articleContainersToReplace) {
      const lineToReplace: AdvancedLine = this._insertArticleHelper.getArticleLine(containerToReplace);
      if (MathUtils.compare(lineToReplace.length, articleWidth) !== 0) {
        return false;
      }
    }
    return true;
  }

  private getRotationAngleByReplacedArticle(): number {
    // apply rotation angle from replaced article
    let rotationAngle: number = 0;
    if (NumberUtils.isNumber(this.articleLocation.rotationAngle)) {
      rotationAngle = this.articleLocation.rotationAngle;
    }
    if (this.projectHelper.considerOriginalAppliedRotationForReplacement()) {
      const appliedRotationAngle: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(
        this._articleContainerToReplace.articleUsage,
        ArticleUsagePlausiPropertyDef.APPLIED_ROTATION_ANGLE
      );
      if (!isNaN(appliedRotationAngle)) {
        rotationAngle += appliedRotationAngle;
      }
    }
    return rotationAngle;
  }

  private getBusbarsToRecreate(): Busbar[] {
    const busbarsToRecreate: Busbar[] = [];
    for (const busbar of this.replaceBusbarHelper.pendingBusbars) {
      const parentContainers: ArticleContainer[] = this.replaceBusbarService.getSortedBusbarParentContainersConnectedToAcceptingId(busbar, this._insertArticleHelper);
      if (ArrayUtils.isNotEmpty(parentContainers)) {
        const articlesUnderBusbar: ArticleContainer[] = this._insertArticleHelper.getConnectedArticlesBetweenContainers(
          parentContainers[0], parentContainers[parentContainers.length - 1]);
        if (ArrayUtils.sourceArrayContainsElementFromTargetArray(articlesUnderBusbar, this._articleContainersToReplace)) {
          ArrayUtils.mergeArrays(busbarsToRecreate, [busbar]);
        }
      }
    }
    return busbarsToRecreate;
  }
}
